import { TFunction } from 'react-i18next';

/**
 * AWS Cognito Error Types
 * These are the error codes that AWS Cognito/Amplify Auth can return
 */
export type CognitoErrorCode =
  | 'UserNotFoundException' // User doesn't exist in Cognito
  | 'NotAuthorizedException' // Wrong password, account disabled, or social account trying password login
  | 'UsernameExistsException' // Username/email already exists
  | 'UserNotConfirmedException' // Email/phone not verified
  | 'ExpiredCodeException' // Verification code expired
  | 'CodeMismatchException' // Verification code doesn't match
  | 'InvalidPasswordException' // Password doesn't meet policy
  | 'InvalidParameterException' // Invalid input parameter
  | 'AliasExistsException' // Email/phone already linked to another account
  | 'LimitExceededException' // Too many requests
  | 'TooManyRequestsException' // Rate limiting
  | 'PasswordResetRequiredException' // Must reset password before login
  | 'ResourceNotFoundException' // Resource not found
  | string; // Other error codes

/**
 * AWS Cognito Error Object Structure
 */
export interface CognitoError {
  code?: CognitoErrorCode;
  message?: string;
  name?: string;
  [key: string]: unknown; // Other properties may exist
}

/**
 * Checks if an error might indicate a social provider account
 * This is a heuristic check - not 100% reliable
 *
 * @param error - Error object from AWS Cognito/Amplify Auth
 * @returns true if error might indicate social account
 */
export const mightBeSocialAccountError = (error: CognitoError): boolean => {
  if (!error) return false;

  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = error.code;

  // Check error message for social provider indicators
  const hasSocialIndicators =
    errorMessage.includes('social') ||
    errorMessage.includes('google') ||
    errorMessage.includes('apple') ||
    errorMessage.includes('facebook') ||
    errorMessage.includes('oauth') ||
    errorMessage.includes('federated');

  // NotAuthorizedException with social indicators might be social account
  if (errorCode === 'NotAuthorizedException' && hasSocialIndicators) {
    return true;
  }

  // UserNotFoundException alone is NOT a reliable indicator of social account
  // It can occur for pre-registered accounts, non-existent accounts, etc.
  // We should NOT return true for UserNotFoundException without additional verification

  return false;
};

export const isAlreadyConfirmedError = (error: CognitoError): boolean =>
  error?.code === 'NotAuthorizedException' &&
  /current status is confirmed/i.test(error?.message || '');

/**
 * Maps AWS Cognito error codes to user-friendly translated messages
 *
 * @param t - Translation function from i18next
 * @param error - Error object from AWS Cognito/Amplify Auth
 * @returns Translated error message
 *
 * @example
 * // UserNotFoundException - User doesn't exist
 * // Can occur for: non-existent accounts, pre-registered accounts not yet set up, deleted accounts
 * getAmplifyAuthErrorMessage(t, { code: 'UserNotFoundException' })
 *
 * // NotAuthorizedException - Authentication failed
 * // Can occur for: wrong password, account disabled, social-only account trying password login
 * getAmplifyAuthErrorMessage(t, { code: 'NotAuthorizedException' })
 */
export const getAmplifyAuthErrorMessage = (
  t: TFunction,
  error: CognitoError,
) => {
  switch (error?.code) {
    case 'UserNotFoundException':
      // ⚠️ NOTE: This error can occur for many reasons:
      // - Account doesn't exist
      // - Pre-registered account not yet fully set up in Cognito
      // - Account was deleted
      // Should NOT automatically assume it's a social account!
      return t('auth.userNotFound');
    case 'NotAuthorizedException':
      // This can occur for:
      // - Wrong password
      // - Account disabled
      // - Social-only account trying password login (sometimes)
      return t('auth.invalidCredentials');
    case 'UsernameExistsException':
      return t('auth.accountExists');
    case 'UserNotConfirmedException':
      return t('auth.userNotConfirmed');
    case 'ExpiredCodeException':
    case 'CodeMismatchException':
      return t('auth.codeExpired');
    case 'InvalidPasswordException':
      // Password doesn't meet policy requirements
      return error?.message || t('errors.text500');
    case 'PasswordResetRequiredException':
      // User must reset password before login
      return error?.message || t('errors.text500');
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      // Rate limiting
      return error?.message || t('errors.text500');
    case 'InvalidParameterException':
      // Invalid input parameter
      return error?.message || t('errors.text500');
    default:
      // For unhandled error codes, return the error message or generic error
      return error?.message || t('errors.text500');
  }
};
